const playbackTimelineBrand: unique symbol = Symbol("playbackTimelineBrand");

type BrandedMilliseconds<Name extends string> = Readonly<{
  value: number;
  readonly [playbackTimelineBrand]: Name;
}>;

export type AbsolutePositionMS = BrandedMilliseconds<"absolute-position-ms">;
export type RawVideoPositionMS = BrandedMilliseconds<"raw-video-position-ms">;
export type DurationMS = BrandedMilliseconds<"duration-ms">;
export type WallClockMS = BrandedMilliseconds<"wall-clock-ms">;

export type PlaybackMilliseconds =
  | AbsolutePositionMS
  | RawVideoPositionMS
  | DurationMS
  | WallClockMS;

function finiteNonNegativeMS(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  return Math.floor(value);
}

function brandedMS<Name extends string>(value: number, name: Name): BrandedMilliseconds<Name> {
  return { value: finiteNonNegativeMS(value), [playbackTimelineBrand]: name } as BrandedMilliseconds<Name>;
}

export function absolutePositionMS(value: number): AbsolutePositionMS {
  return brandedMS(value, "absolute-position-ms");
}

export function rawVideoPositionMS(value: number): RawVideoPositionMS {
  return brandedMS(value, "raw-video-position-ms");
}

export function durationMS(value: number): DurationMS {
  return brandedMS(value, "duration-ms");
}

export function wallClockMS(value: number): WallClockMS {
  return brandedMS(value, "wall-clock-ms");
}

export function negativeInfinityWallClockMS(): WallClockMS {
  return { value: Number.NEGATIVE_INFINITY, [playbackTimelineBrand]: "wall-clock-ms" } as WallClockMS;
}

export function ms(value: PlaybackMilliseconds): number {
  return value.value;
}

export function rawBufferedAheadMS(
  bufferedEndMS: RawVideoPositionMS,
  playheadMS: RawVideoPositionMS,
): DurationMS {
  return durationMS(ms(bufferedEndMS) - ms(playheadMS));
}

export function generationRelativeMediaTimeSeconds(
  absoluteSeconds: number,
  originSeconds: number,
): number {
  if (!Number.isFinite(absoluteSeconds) || !Number.isFinite(originSeconds)) {
    return 0;
  }
  return Math.max(0, absoluteSeconds - Math.max(0, originSeconds));
}

export type TimelineSample = Readonly<{
  offsetMS: number;
  absoluteMS: number;
}>;

/**
 * Asserts the re-anchor invariant from #846: `offset + video.currentTime` must
 * stay continuous across an anchor change.
 *
 * A generation replacement moves the anchor before the media element rebases
 * its own clock, so for one window `offset + currentTime` counts the elapsed
 * span twice and reports a position far ahead of the real playhead. The tell is
 * an anchor change paired with a forward step the wall clock cannot explain.
 *
 * Only meaningful when the anchor actually moved; a steady anchor makes any
 * forward step ordinary playback, and a commanded seek is the caller's to
 * exempt before asking.
 */
export function reanchorDoubleCountsElapsed(
  previous: TimelineSample,
  next: TimelineSample,
  toleranceMS: number,
): boolean {
  if (next.offsetMS === previous.offsetMS) {
    return false;
  }
  return next.absoluteMS - previous.absoluteMS > toleranceMS;
}
